<template>
  <ResponsiveLayout
    sticky-actions
    :progress="progress"
    :show-lang-picker="false"
  >
    <template #header_start>
      <ButtonBack @click="emit('back')" />
    </template>
    <div class="kds-main-wrapper">
      <div class="kds-content-block">
        <h3
          class="kds-typography-title-xlarge"
          v-html="$t('eu__self_test_goal__question')"
        />
      </div>
      <div class="self-test">
        <fieldset class="form-container">
          <div
            v-for="option in goalOptions"
            :key="option.value"
            :class="[
              'quiz-radio',
              { 'quiz-radio--checked': goal === option.value },
            ]"
            @click="setGoal(option.value)"
          >
            <label class="quiz-radio__label kds-typography-title-medium">
              {{ $t(option.label) }}
            </label>
            <div class="quiz-radio__layout">
              <custom-radio
                :checked="goal === option.value"
                :data-qa="'goal-' + option.value"
                :value="option.value"
              />
            </div>
          </div>
        </fieldset>
        <div
          v-if="goal === 'other'"
          class="goal-freetext-input"
        >
          <TextareaField
            v-model="goalFreetext"
            :placeholder="$t('eu__self_test_goal__other_placeholder')"
            class="kds-typography-paragraph-large"
            data-qa="goal-freetext-input"
            maxlength="255"
            rows="3"
          />
        </div>
      </div>
    </div>
    <template #actions>
      <BaseButton
        :disabled="!isValid"
        :text="$t('generic_action_continue')"
        data-qa="submit"
        @click="next"
      />
    </template>
  </ResponsiveLayout>
</template>

<script setup>
import useSelfTestStore from '@shared/modules/assessment/stores/useSelfTestStore.js'
import { GOAL_OPTIONS as goalOptions } from '@shared/modules/assessment/config/selfTestOptions'
import CustomRadio from '@shared/components/CustomRadio.vue'
import BaseButton from '@shared/components/BaseButton.vue'
import ResponsiveLayout from '@shared/components/ResponsiveLayout.vue'
import ButtonBack from '@shared/components/ButtonBack.vue'
import { computed, ref } from 'vue'
import { useI18n } from 'petite-vue-i18n'
import TextareaField from '@shared/components/form/TextareaField.vue'

const props = defineProps({
  progress: {
    type: Number,
    default: null,
  },
  handleNextEvent: {
    type: Function,
    default: () => {},
  },
})

const emit = defineEmits(['back'])

const selfTestStore = useSelfTestStore()
const { t } = useI18n()

const goal = ref(selfTestStore.goal)
const goalFreetext = ref(selfTestStore.answers.goal_freetext?.value ?? '')

const setGoal = (value) => {
  goal.value = value
  if (value !== 'other') goalFreetext.value = ''
}

const isValid = computed(() => Boolean(goal.value))

const next = () => {
  const option = goalOptions.find((o) => o.value === goal.value)
  if (!option) return
  const questionText = t('eu__self_test_goal__question')
  selfTestStore.answer('goal', {
    value: option.value,
    text: t(option.label),
    questionText,
  })
  // The contract rejects a blank text; whitespace is not an answer.
  const freetext = goalFreetext.value.trim()
  if (option.value === 'other' && freetext) {
    selfTestStore.answer('goal_freetext', {
      value: freetext,
      text: freetext,
      questionText,
    })
  } else {
    selfTestStore.clearAnswer('goal_freetext')
  }
  props.handleNextEvent()
}
</script>

<style lang="scss">
@use '@shared/modules/assessment/views/css/SelfTest.scss' as *;
</style>
